import { useAuth } from '@ixigo-packages/login/react';
import { useEffect, useRef } from 'react';
import { CbccLokiKey, logCbccLokiInfo } from '@/lib/loki-logger';
import { useCbccFlow } from '../hooks/use-cbcc-flow';
import { cbccRouteApi } from '../route-api';
import { fetchCvp } from '../services/fetch-cvp';
import { consumePendingFlow } from '../utils/pending-flow';
import { resolveUserFlow } from '../utils/resolve-user-flow';

// Null-render side-effect component, mounted once in the layout. Completes the
// "log in, then continue without a second tap" handshake: when a logged-out
// user taps an apply CTA, CbccCta arms the pending-flow flag and opens login;
// after login this effect fires once — consuming the flag and dispatching the
// user's flow (form / onboard / manage-card) via the same useCbccFlow the CTA
// uses.
//
// Why it fetches CVP DIRECTLY instead of waiting on the loader's userFlow:
// CbccCta's post-login callback fires `router.invalidate()`, but invalidate's
// promise resolves BEFORE the fresh loaderData is committed to router.state
// (the commit runs in a later transition — CbccCta.recoverAndDispatch documents
// the same race). So the loader's `userFlow` read here can still be a stale
// `null` the instant login completes; dispatching against it no-ops and the
// user sees nothing happen (the reported "no auto-redirect after login" bug).
// Fetching CVP ourselves yields the fresh flow with the just-synced `at`,
// independent of loader-commit timing. Still gated on the pending-flow flag
// (single-shot) so a normal logged-in landing never auto-redirects.
export function CbccPostLoginResume() {
	const { isLoggedIn } = useAuth();
	const { dispatchFlow } = useCbccFlow();
	// Fast path: if the loader already committed a usable flow, dispatch it
	// without a refetch. Read at fire time inside the effect.
	const { userFlow } = cbccRouteApi.useLoaderData();
	// Single-shot guard: consumePendingFlow clears the flag synchronously, but the
	// refetch resolves later — this stops a re-run from racing the in-flight fetch.
	const resuming = useRef(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the trigger is the login transition (isUserLoggedIn); dispatchFlow/userFlow are read at fire time.
	useEffect(() => {
		if (!isLoggedIn || resuming.current) return;
		if (!consumePendingFlow()) return;
		resuming.current = true;

		// Fast path: loader already has a real flow → dispatch immediately.
		if (userFlow != null && dispatchFlow(userFlow)) {
			logCbccLokiInfo(CbccLokiKey.POST_LOGIN_RESUME, {
				userFlow,
				source: 'loader',
			});
			return;
		}

		// Loader flow is stale/null (the invalidate→commit race). Fetch CVP
		// directly with the fresh `at` and dispatch the real flow.
		fetchCvp()
			.then(({ data }) => {
				const fresh = resolveUserFlow(data.screenData);
				logCbccLokiInfo(CbccLokiKey.POST_LOGIN_RESUME, {
					userFlow: fresh ?? 'null',
					source: 'refetch',
				});
				dispatchFlow(fresh);
			})
			.catch(() => {
				// Refetch failed — dispatchFlow's null branch self-heals (it
				// re-invalidates the loader to refresh the page state).
				logCbccLokiInfo(CbccLokiKey.POST_LOGIN_RESUME, {
					userFlow: 'null',
					source: 'refetch-failed',
				});
				dispatchFlow(null);
			});
	}, [isLoggedIn]);

	return null;
}
